import React, { useEffect, useState, useRef } from 'react';
import { Camera, User, Sparkles, ArrowRightIcon, ChevronLeft, Mic, Upload, PenLine, X } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { useProfileState } from '../components/ProfileStateContext';
import { ROLES } from '../data/roles';
import { SECTORS } from '../data/sectors';
interface Props {
  onContinue: () => void;
  onBack?: () => void;
}
type InputMode = 'upload' | 'voice' | 'text';
export function OnboardingRecap({
  onContinue,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const {
    updateDraft,
    firstName: initialFirstName,
    lastName: initialLastName,
    avatarUrl,
    roleId,
    roleOther,
    orgName,
    sectorId,
    selectedThemeIds,
    project
  } = useProfileState();
  const [mounted, setMounted] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(avatarUrl);
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [textInput, setTextInput] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoUrl(url);
      updateDraft({
        avatarUrl: url
      });
    }
  };
  // Resolve display values from context
  const roleLabel = roleId === 'other' ? roleOther : roleId ? t(ROLES.find(r => r.id === roleId)?.nameKey || '') : '';
  const hasProject = !!project;
  useEffect(() => {
    const tm = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(tm);
  }, []);
  useEffect(() => {
    updateDraft({
      firstName,
      lastName
    });
  }, [firstName, lastName, updateDraft]);
  const handleAddProject = () => {
    setIsAddProjectOpen(true);
  };
  const handleSubmitProject = () => {
    if (textInput.trim().length < 10) return;
    updateDraft({
      project: {
        title: textInput.trim().slice(0, 60),
        description: textInput.trim(),
        topicIds: []
      }
    });
    setIsAddProjectOpen(false);
    setTextInput('');
  };
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-654">
      {onBack && <button onClick={onBack} aria-label="Back" className="absolute top-6 left-6 z-50 w-10 h-10 rounded-full bg-[#1A1A1A]/80 backdrop-blur-md border border-[#2A2A2A] text-[#888888] flex items-center justify-center hover:text-white transition-colors" data-id="element-655">
          <ChevronLeft className="w-6 h-6 -ml-0.5" strokeWidth={2} data-id="element-656" />
        </button>}
      <div className="flex-1 overflow-y-auto px-5 pt-20 pb-40" data-id="element-657">
        {/* Progress Bar */}
        <div className="w-full transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '0ms'
      }} data-id="element-658">
          <div className="w-full h-1 bg-[#1A1A1A] rounded-full overflow-hidden" data-id="element-659">
            <div className="h-full bg-[#C5D82D] w-[100%] rounded-full transition-all duration-500" data-id="element-660" />
          </div>
          <div className="flex items-center justify-end mt-1.5" data-id="element-661">
            <span className="text-[#888888] text-[12px]" data-id="element-662">
              {t('recap.progress')}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="mt-6 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '100ms'
      }} data-id="element-663">
          <h1 className="text-[20px] text-white font-semibold" data-id="element-664">
            {t('recap.heading')}
          </h1>
          <p className="text-[14px] text-[#AAAAAA] mt-1 max-w-[280px]" data-id="element-665">
            {t('recap.subtext')}
          </p>
        </div>

        {/* Avatar */}
        <div className="flex justify-center mt-6 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '200ms'
      }} data-id="element-666">
          <button onClick={() => photoInputRef.current?.click()} aria-label={t('a11y.uploadPhoto')} className="relative group active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D] rounded-full" data-id="element-667">
            <input ref={photoInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={handlePhotoChange} data-id="element-668" />
            <div className="w-20 h-20 bg-[#1A1A1A] rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:border-[#C5D82D]/50 border border-[#2A2A2A]" data-id="element-669">
              {photoUrl ? <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" data-id="element-670" /> : <User className="w-8 h-8 text-[#888888]" data-id="element-671" />}
            </div>
            <div className="absolute bottom-0 right-0 w-6 h-6 bg-[#C5D82D] rounded-full flex items-center justify-center shadow-lg border-2 border-[#0D0D0D]" data-id="element-672">
              <Camera className="w-3 h-3 text-[#0D0D0D]" data-id="element-673" />
            </div>
          </button>
        </div>

        {/* Email (pre-filled, non-editable) */}
        <div className="mt-5 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '230ms'
      }} data-id="element-674">
          <label className="text-[12px] text-[#888888] tracking-wide" data-id="element-675">
            {t('recap.emailLabel')}
          </label>
          <input type="email" readOnly value="mario.rossi@example.com" className="mt-2 w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-[#6A6A6A] text-[15px] border border-[#2A2A2A] cursor-not-allowed select-all" tabIndex={-1} data-id="element-676" />
        </div>

        {/* Name (optional) */}
        <div className="mt-5 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '280ms'
      }} data-id="element-677">
          <label className="text-[12px] text-[#888888] tracking-wide" data-id="element-678">
            {t('recap.nameLabel')}
          </label>
          <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-679">
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('recap.firstNamePh')} className="min-w-0 w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-white placeholder:text-[#555555] text-[15px] focus:outline-none focus:ring-1 focus:ring-[#2A2A2A] transition-all" data-id="element-680" />
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('recap.lastNamePh')} className="min-w-0 w-full bg-[#1A1A1A] py-3.5 px-4 rounded-xl text-white placeholder:text-[#555555] text-[15px] focus:outline-none focus:ring-1 focus:ring-[#2A2A2A] transition-all" data-id="element-681" />
          </div>
        </div>

        {/* Profile Card */}
        <div className="mt-5 bg-[#1A1A1A] rounded-xl p-5 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '300ms'
      }} data-id="element-682">
          {/* Role */}
          <div data-id="element-683">
            <h3 className="text-[12px] text-[#888888] tracking-wide mb-1.5" data-id="element-684">
              {t('recap.roleLabel')}
            </h3>
            <p className="text-[15px] text-white" data-id="element-685">{roleLabel || '—'}</p>
            {orgName && <p className="text-[13px] text-[#AAAAAA] mt-1" data-id="element-686">{orgName}</p>}
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-[#242424] my-4" data-id="element-687" />

          {/* Themes */}
          <div data-id="element-688">
            <h3 className="text-[12px] text-[#888888] tracking-wide mb-2" data-id="element-689">
              {t('recap.themesLabel')}
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-690">
              {selectedThemeIds.length > 0 ? selectedThemeIds.map(theme => <span key={theme} className="px-3 py-1.5 bg-[#242424] text-white text-[12px] rounded-full" data-id="element-691">
                    {theme}
                  </span>) : <span className="text-[13px] text-[#666666]" data-id="element-692">—</span>}
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-[#242424] my-4" data-id="element-693" />

          {/* Project */}
          <div data-id="element-694">
            <h3 className="text-[12px] text-[#888888] tracking-wide mb-2" data-id="element-695">
              {t('recap.projectLabel')}
            </h3>
            {hasProject && project ? <div data-id="element-696">
                <p className="text-[15px] text-white" data-id="element-697">{project.title}</p>
                <p className="text-[13px] text-[#AAAAAA] mt-1" data-id="element-698">
                  {project.description}
                </p>
              </div> : <button onClick={handleAddProject} className="w-full bg-[#1A1A1A] border border-dashed border-[#C5D82D]/30 rounded-xl p-4 flex items-center justify-between transition-colors hover:border-[#C5D82D]/60" data-id="element-699">
                <span className="flex items-center gap-2 text-left" data-id="element-700">
                  <Sparkles className="w-4 h-4 text-[#C5D82D] shrink-0" strokeWidth={2} data-id="element-701" />
                  <span className="text-[13px] text-white" data-id="element-702">
                    {t('recap.addProjectCallout')}
                  </span>
                </span>
                <ArrowRightIcon className="w-4 h-4 text-[#C5D82D] shrink-0" strokeWidth={2} data-id="element-703" />
              </button>}
          </div>
        </div>

        {/* Seed Indicator */}
        
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl px-6 pb-8 pt-4 transition-all duration-700 ease-out z-40" style={{
      opacity: mounted ? 1 : 0,
      transitionDelay: '500ms'
    }} data-id="element-704">
        <button onClick={onContinue} className="w-full py-4 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-[#d4e63a]" data-id="element-705">
          {t('recap.cta')}
          <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-706" />
        </button>
      </div>

      {/* Add Project Sheet */}
      {isAddProjectOpen && <div className="absolute inset-0 z-50 flex flex-col justify-end" data-id="element-707">
          <div className="absolute inset-0 bg-black/70 animate-in fade-in duration-200" onClick={() => setIsAddProjectOpen(false)} data-id="element-708" />
          <div className="relative bg-[#141414] rounded-t-2xl border-t border-[#2A2A2A] pt-6 pb-8 px-6 max-h-[70%] overflow-y-auto" style={{
        animation: 'drawerSlideUp 280ms cubic-bezier(0.16, 1, 0.3, 1) both'
      }} data-id="element-709">
            <div className="flex items-center justify-between mb-5" data-id="element-710">
              <h2 className="text-[15px] text-white font-medium" data-id="element-711">
                {t('projects.heading')}
              </h2>
              <button onClick={() => setIsAddProjectOpen(false)} className="w-8 h-8 bg-[#1A1A1A] rounded-full flex items-center justify-center text-[#888888] border border-[#2A2A2A]" aria-label={t('common.close')} data-id="element-712">
                <X className="w-5 h-5" data-id="element-713" />
              </button>
            </div>

            {/* Mode Toggle */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-1 flex mb-6 w-fit" data-id="element-714">
              <button onClick={() => setInputMode('upload')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'upload' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999]'}`} data-id="element-715">
                <Upload className="w-4 h-4" strokeWidth={2} data-id="element-716" />
                {t('projects.tab.upload')}
              </button>
              <button onClick={() => setInputMode('voice')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'voice' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999]'}`} data-id="element-717">
                <Mic className="w-4 h-4" strokeWidth={2} data-id="element-718" />
                {t('projects.tab.voice')}
              </button>
              <button onClick={() => setInputMode('text')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'text' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999]'}`} data-id="element-719">
                <PenLine className="w-4 h-4" strokeWidth={2} data-id="element-720" />
                {t('projects.tab.write')}
              </button>
            </div>

            {/* Text Input */}
            {inputMode === 'text' && <div className="space-y-3" data-id="element-721">
                <textarea value={textInput} onChange={e => setTextInput(e.target.value)} placeholder={t('project.placeholder')} className="w-full bg-[#1A1A1A] p-4 rounded-xl border border-[#2A2A2A] text-white placeholder:text-[#444444] focus:outline-none focus:border-[#2A2A2A] transition-colors text-[15px] resize-none h-[120px]" data-id="element-722" />
                <button onClick={handleSubmitProject} disabled={textInput.trim().length < 10} className={`w-full py-3 rounded-full text-[14px] font-semibold transition-all ${textInput.trim().length >= 10 ? 'bg-[#C5D82D] text-[#0D0D0D] hover:bg-[#d4e63a]' : 'bg-[#1A1A1A] text-[#555555] opacity-50 cursor-not-allowed'}`} data-id="element-723">
                  {t('projects.save')}
                </button>
              </div>}

            {inputMode === 'upload' && <div className="text-center py-8 text-[#666666] text-[14px]" data-id="element-724">
                {t('projects.upload.hint')}
              </div>}

            {inputMode === 'voice' && <div className="text-center py-8 text-[#666666] text-[14px]" data-id="element-725">
                {t('project.maxSeconds')}
              </div>}
          </div>
          <style dangerouslySetInnerHTML={{
        __html: `
            @keyframes drawerSlideUp {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: none; opacity: 1; }
            }
          `
      }} data-id="element-726" />
        </div>}
    </div>;
}